import { FootnoteEntryPlugin } from '@alpod-ai-writer/plate-plugins/footnotes/react';
import { ImagePlugin } from '@alpod-ai-writer/plate-plugins/media/react';
import { acceptSuggestion, getSuggestionKey, keyId2SuggestionId, rejectSuggestion, type TResolvedSuggestion, type TSuggestionText } from '@alpod-ai-writer/plate-plugins/suggestion';
import { SuggestionPlugin } from '@alpod-ai-writer/plate-plugins/suggestion/react';
import { TablePlugin } from '@alpod-ai-writer/plate-plugins/table/react';
import {
    ElementApi,
    type NodeEntry,
    type Path,
    PathApi,
    type TElement,
    TextApi,
} from '@udecode/plate';
import {
    ParagraphPlugin,
    useEditorPlugin,
    useReadOnly,
    useStoreSelect,
} from '@udecode/plate/react';
import { HEADING_KEYS } from '@udecode/plate-heading';
import { TocPlugin } from '@udecode/plate-heading/react';
import { ListStyleType } from '@udecode/plate-indent-list';
import { IndentListPlugin } from '@udecode/plate-indent-list/react';
import { EquationPlugin } from '@udecode/plate-math/react';
import { Avatar } from 'flowbite-react';
import { CheckIcon, XIcon } from 'lucide-react';
import React, { useMemo } from 'react';

import { getInitialsFromName } from '../../../lib/identity.utils';
import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { ATTACHMENT_HEADING_KEY } from '../../plugins/attachmentHeadingPlugin';
import { suggestionPlugin } from '../../plugins/suggestionPlugin';
import {
    discussionStore,
    type TDiscussion
} from './blockDiscussion.component';
import { Button } from './button.component';
import { formatCommentDate, type TComment } from './comment.component';

export interface ResolvedSuggestion extends TResolvedSuggestion {
    comments: TComment[];
}

export const BLOCK_SUGGESTION = '__block__';

export const TYPE_TEXT_MAP: Record<string, (node?: TElement) => string> = {
    [EquationPlugin.key]: () => 'vzorec',
    [HEADING_KEYS.h1]: () => 'nadpis 1',
    [HEADING_KEYS.h2]: () => 'nadpis 2',
    [HEADING_KEYS.h3]: () => 'nadpis 3',
    [ATTACHMENT_HEADING_KEY]: () => 'nadpis přílohy',
    [ImagePlugin.key]: () => 'obrázek',
    [ParagraphPlugin.key]: (node) => {
        if (node?.[IndentListPlugin.key] === ListStyleType.Decimal)
            return 'číslovaný seznam';
        if (node?.[IndentListPlugin.key] === ListStyleType.Disc) return 'nečíslovaný seznam';

        return 'odstavec';
    },
    [TablePlugin.key]: () => 'tabulka',
    [TocPlugin.key]: () => 'obsah',
};

export const BlockSuggestionCard = ({
    idx,
    suggestion,
}: {
  idx: number;
  isLast: boolean;
  suggestion: ResolvedSuggestion;
}) => {
    const { resolveUserIdentity } = useDocumentEditor();
    const { api, editor } = useEditorPlugin(SuggestionPlugin);
    const readOnly = useReadOnly();

    const accept = (suggestion: ResolvedSuggestion) => {
        if (readOnly) {
            const onSuggestionAccepted = editor.getOption(FootnoteEntryPlugin, 'onSuggestionAccepted');
            onSuggestionAccepted?.(editor as any, suggestion.keyId, suggestion.suggestionId);
            return;
        }
        api.suggestion.withoutSuggestions(() => {
            acceptSuggestion(editor, suggestion);
        });
    };

    const reject = (suggestion: ResolvedSuggestion) => {
        if (readOnly) {
            const onSuggestionRejected = editor.getOption(FootnoteEntryPlugin, 'onSuggestionRejected');
            onSuggestionRejected?.(editor as any, suggestion.keyId, suggestion.suggestionId);
            return;
        }
        api.suggestion.withoutSuggestions(() => {
            rejectSuggestion(editor, suggestion);
        });
    };

    const suggestionText2Array = (text: string) => {
        if (text === BLOCK_SUGGESTION) return ['line breaks'];

        return text.split(BLOCK_SUGGESTION).filter(Boolean);
    };

    const userInfo = resolveUserIdentity(suggestion.userId);

    return (
        <div
            key={`${suggestion.suggestionId}-${idx}`}
            className="relative border-t border-gray-200 first:border-none"
        >
            <div className="flex flex-col p-3">
                <div className="relative flex items-center">
                    <Avatar
                        alt={userInfo?.name ?? 'N/A'}
                        title={userInfo?.name ?? 'N/A'}
                        placeholderInitials={getInitialsFromName(userInfo?.name ?? '')}
                        size='xs'
                        rounded
                        style={{
                            backgroundColor: '#5048e5',
                            borderRadius: '1000px'
                        }}
                        theme={{
                            root: {
                                base: 'relative',
                                initials: {
                                    base: 'flex flex-row items-center justify-center bg-inherit',
                                    text: 'text-white text-xs'
                                }
                            },
                        }}
                    />
                    <h4 className="mx-2 text-sm leading-none font-semibold">
                        {userInfo?.name ?? 'Neznámý uživatel'}
                    </h4>
                    <div className="text-xs leading-none text-gray-600 grow">
                        <span className="mr-1">
                            {formatCommentDate(new Date(suggestion.createdAt))}
                        </span>
                    </div>
                    <div className="flex flex-row items-center">
                        <Button
                            variant="ghost"
                            className="h-8 p-2 text-gray-800 hover:bg-lime-100 hover:text-lime-800"
                            onClick={() => accept(suggestion)}
                        >
                            <CheckIcon className="size-4" />
                        </Button>

                        <Button
                            variant="ghost"
                            className="h-8 p-2 text-gray-800 hover:bg-red-50 hover:text-red-800"
                            onClick={() => reject(suggestion)}
                        >
                            <XIcon className="size-4" />
                        </Button>
                    </div>
                </div>

                <div className="relative mt-1 mb-4 pl-[32px]">
                    <div className="flex flex-col gap-2">
                        {suggestion.type === 'remove' && (
                            <React.Fragment>
                                {suggestionText2Array(suggestion.text!).map((text, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <span className="text-sm text-red-700">
                                            Smazat:
                                        </span>

                                        <span key={index} className="text-sm">
                                            {formatText(text)}
                                        </span>
                                    </div>
                                ))}
                            </React.Fragment>
                        )}

                        {suggestion.type === 'insert' && (
                            <React.Fragment>
                                {suggestionText2Array(suggestion.newText!).map(
                                    (text, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <span className="text-sm text-primary-700">
                                                Vložit:
                                            </span>

                                            <span key={index} className="text-sm">
                                                {formatText(text) || '↵'}
                                            </span>
                                        </div>
                                    )
                                )}
                            </React.Fragment>
                        )}

                        {suggestion.type === 'replace' && (
                            <div className="flex flex-col gap-2">
                                {suggestionText2Array(suggestion.text!).map((text, index) => (
                                    <React.Fragment key={index}>
                                        <div key={index} className="flex items-start gap-2">
                                            <span className="text-sm text-red-700">
                                                {index === 0 ? 'Nahradit:' : 'Smazat:'}
                                            </span>
                                            <span className="text-sm">{formatText(text) || '↵'}</span>
                                        </div>
                                    </React.Fragment>
                                ))}

                                {suggestionText2Array(suggestion.newText!).map(
                                    (text, index) => (
                                        <React.Fragment key={index}>
                                            <div
                                                key={index}
                                                className="flex items-start gap-2"
                                            >
                                                <span className="text-sm text-primary-700">Obsahem:</span>
                                                <span className="text-sm">{formatText(text) || '↵'}</span>
                                            </div>
                                        </React.Fragment>
                                    )
                                )}
                            </div>
                        )}

                        {suggestion.type === 'update' && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                    {Object.keys(suggestion.properties).map((key) => (
                                        <span key={key}>Un{key}</span>
                                    ))}

                                    {Object.keys(suggestion.newProperties).map((key) => (
                                        <span key={key}>
                                            {key.charAt(0).toUpperCase() + key.slice(1)}
                                        </span>
                                    ))}
                                </span>
                                <span className="text-sm">{suggestion.newText}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const useResolveSuggestion = (
    suggestionNodes: NodeEntry<TElement | TSuggestionText>[],
    blockPath: Path
) => {
    const discussions = useStoreSelect(
        discussionStore,
        (state) => state.discussions
    );

    const { api, editor, getOption, setOption } =
    useEditorPlugin(suggestionPlugin);

    suggestionNodes.forEach(([node]) => {
        const id = api.suggestion.nodeId(node);
        const map = getOption('uniquePathMap');

        if (!id) return;

        const previousPath = map.get(id);

        // If there are no suggestion nodes in the corresponding path in the map, then update it.
        if (PathApi.isPath(previousPath)) {
            const nodes = api.suggestion.node({ id, at: previousPath, isText: true });
            const parentNode = api.node(previousPath);
            let lineBreakId: string | null = null;

            if (parentNode && ElementApi.isElement(parentNode[0])) {
                lineBreakId = api.suggestion.nodeId(parentNode[0]) ?? null;
            }

            if (!nodes && lineBreakId !== id) {
                return setOption('uniquePathMap', new Map(map).set(id, blockPath));
            }

            return;
        }
        setOption('uniquePathMap', new Map(map).set(id, blockPath));
    });

    const resolvedSuggestion: ResolvedSuggestion[] = useMemo(() => {
        const map = getOption('uniquePathMap');

        if (suggestionNodes.length === 0) return [];

        const suggestionIds = new Set(
            suggestionNodes
                .flatMap(([node]) => {
                    if (TextApi.isText(node)) {
                        const dataList = api.suggestion.dataList(node);
                        const includeUpdate = dataList.some(
                            (data) => data.type === 'update'
                        );

                        if (!includeUpdate) return api.suggestion.nodeId(node);

                        return dataList
                            .filter((data) => data.type === 'update')
                            .map((d) => d.id);
                    }
                    if (ElementApi.isElement(node)) {
                        return api.suggestion.nodeId(node);
                    }
                })
                .filter(Boolean)
        );

        const res: ResolvedSuggestion[] = [];

        suggestionIds.forEach((id) => {
            if (!id) return;

            const path = map.get(id);

            if (!path || !PathApi.isPath(path)) return;
            if (!PathApi.equals(path, blockPath)) return;

            const entries = [
                ...editor.api.nodes<TElement | TSuggestionText>({
                    at: [],
                    mode: 'all',
                    match: (n) =>
                        (n[SuggestionPlugin.key] && n[getSuggestionKey(id)]) ||
            api.suggestion.nodeId(n as TElement) === id,
                }),
            ];

            // move line break to the end
            entries.sort(([, path1], [, path2]) => {
                return PathApi.isChild(path1, path2) ? -1 : 1;
            });

            let newText = '';
            let text = '';
            let properties: any = {};
            let newProperties: any = {};

            // overlapping suggestion
            entries.forEach(([node]) => {
                if (TextApi.isText(node)) {
                    const dataList = api.suggestion.dataList(node);

                    dataList.forEach((data) => {
                        if (data.id !== id) return;

            switch (data.type) {
              case 'insert': {
                  newText += node.text;

                  break;
              }
              case 'remove': {
                  text += node.text;

                  break;
              }
              case 'update': {
                  properties = {
                      ...properties,
                      ...data.properties,
                  };

                  newProperties = {
                      ...newProperties,
                      ...data.newProperties,
                  };

                  newText += node.text;

                  break;
              }
              // No default
            }
                    });
                } else {
                    const lineBreakData = api.suggestion.isBlockSuggestion(node)
                        ? api.suggestion.suggestionData(node) as { id?: string; type?: string; isLineBreak?: boolean } | undefined
                        : undefined;

                    if (lineBreakData?.id !== keyId2SuggestionId(id)) return;
                    if (lineBreakData.type === 'insert') {
                        newText += lineBreakData.isLineBreak
                            ? BLOCK_SUGGESTION
                            : BLOCK_SUGGESTION + TYPE_TEXT_MAP[node.type](node);
                    } else if (lineBreakData.type === 'remove') {
                        text += lineBreakData.isLineBreak
                            ? BLOCK_SUGGESTION
                            : BLOCK_SUGGESTION + TYPE_TEXT_MAP[node.type](node);
                    }
                }
            });

            if (entries.length === 0) return;

            const nodeData = api.suggestion.suggestionData(entries[0][0]);

            if (!nodeData) return;

            // const comments = data?.discussions.find((d) => d.id === id)?.comments;
            const comments = discussions.find((s: TDiscussion) => s.id === id)?.comments || [];
            const createdAt = new Date(nodeData.createdAt);

            const keyId = getSuggestionKey(id);

            if (nodeData.type === 'update') {
                return res.push({
                    comments,
                    createdAt,
                    keyId,
                    newProperties,
                    newText,
                    properties,
                    suggestionId: keyId2SuggestionId(id),
                    type: 'update',
                    userId: nodeData.userId,
                });
            }
            if (newText.length > 0 && text.length > 0) {
                return res.push({
                    comments,
                    createdAt,
                    keyId,
                    newText,
                    suggestionId: keyId2SuggestionId(id),
                    text,
                    type: 'replace',
                    userId: nodeData.userId,
                });
            }
            if (newText.length > 0) {
                return res.push({
                    comments,
                    createdAt,
                    keyId,
                    newText,
                    suggestionId: keyId2SuggestionId(id),
                    type: 'insert',
                    userId: nodeData.userId,
                });
            }
            if (text.length > 0) {
                return res.push({
                    comments,
                    createdAt,
                    keyId,
                    suggestionId: keyId2SuggestionId(id),
                    text,
                    type: 'remove',
                    userId: nodeData.userId,
                });
            }
        });

        return res;
    }, [
        api.suggestion,
        blockPath,
        discussions,
        editor.api,
        getOption,
        suggestionNodes,
    ]);

    return resolvedSuggestion;
};

export const isResolvedSuggestion = (
    suggestion: ResolvedSuggestion | TDiscussion
): suggestion is ResolvedSuggestion => {
    return 'suggestionId' in suggestion;
};


function formatText(text: string) {
    if (text === ' ') {
        return <><span className="text-primary-500 font-sans">·</span> <span className='text-gray-400'>(mezera)</span></>;
    }

    if (text === '\u00A0') {
        return <><span className="text-primary-500 font-sans">°</span> <span className='text-gray-400'>(nezalomitelná mezera)</span></>;
    }

    if (text === '\n') {
        return <><span className="text-primary-500 font-sans">↵</span> <span className='text-gray-400'>(odřádkování)</span></>;
    }

    // Split text and replace symbols with JSX elements
    const parts = text.split(/( |\u00A0|\n)/);
    
    return (
        <>
            {parts.map((part, index) => {
                if (part === ' ') {
                    return <span key={index} className="text-primary-500 font-sans">·</span>;
                }
                if (part === '\u00A0') {
                    return <span key={index} className="text-primary-500 font-sans">°</span>;
                }
                if (part === '\n') {
                    return <span key={index} className="text-primary-500 font-sans">↵</span>;
                }
                return part;
            })}
        </>
    );
}
